import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, InputNumber, Button, Tabs, Row, Col, message, Space, Select, Card, List, Typography, Radio, Rate, App } from 'antd';
import { ShopOutlined, PhoneOutlined, MailOutlined, ClockCircleOutlined, ReloadOutlined, PictureOutlined, BgColorsOutlined, DeleteOutlined, PlusOutlined, StarOutlined, YoutubeOutlined, VideoCameraOutlined } from '@ant-design/icons';
import type { TabsProps } from 'antd';
import { shopConfig, saveShopConfig, resetShopConfig, ShopConfig, themePresets } from '../config/shop.config';
import ImageUpload from './ImageUpload';
import { shopLandingConfigApi, ShopLandingConfig } from '../services/shopLandingConfigApi';

const { TextArea } = Input;
const { TabPane } = Tabs;
const { Title } = Typography;

interface ShopConfigModalProps {
  visible: boolean;
  onClose: () => void;
}

const ShopConfigModal: React.FC<ShopConfigModalProps> = ({ visible, onClose }) => {
  const { message: messageApi } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [galleryImages, setGalleryImages] = useState<any[]>([]);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newImageTitle, setNewImageTitle] = useState('');
  const [newImageCategory, setNewImageCategory] = useState('Engine Work');
  const [newMediaType, setNewMediaType] = useState<'image' | 'video'>('image');
  const [newVideoProvider, setNewVideoProvider] = useState<'youtube' | 'vimeo'>('youtube');
  const [newVideoId, setNewVideoId] = useState('');

  // Testimonials state
  const [testimonials, setTestimonials] = useState<any[]>([]);
  const [newTestimonialName, setNewTestimonialName] = useState('');
  const [newTestimonialText, setNewTestimonialText] = useState('');
  const [newTestimonialRating, setNewTestimonialRating] = useState(5);
  const [newTestimonialDate, setNewTestimonialDate] = useState('');

  useEffect(() => {
    if (visible) {
      loadConfig();
    }
  }, [visible, form]);

  const loadConfig = async () => {
    try {
      setLoading(true);

      // Try to migrate from localStorage first
      const hasLocalStorage = localStorage.getItem('cnf_shop_config');
      if (hasLocalStorage) {
        await shopLandingConfigApi.migrateFromLocalStorage();
      }

      // Load from backend API
      const apiConfig = await shopLandingConfigApi.get();

      // Populate form with API config
      form.setFieldsValue({
        name: apiConfig.businessName || '',
        tagline: apiConfig.tagline || '',
        phone: apiConfig.phone || '',
        email: apiConfig.email || '',
        street: apiConfig.address?.street || '',
        city: apiConfig.address?.city || '',
        state: apiConfig.address?.state || '',
        zip: apiConfig.address?.zip || '',
        weekday: apiConfig.hours?.weekday || '8:00 AM - 6:00 PM',
        saturday: apiConfig.hours?.saturday || '9:00 AM - 4:00 PM',
        sunday: apiConfig.hours?.sunday || 'Closed',
        prePurchasePrice: apiConfig.prePurchasePrice || 250,
        logo: apiConfig.branding?.logo || '',
        heroBackground: apiConfig.branding?.heroBackground || '',
        theme: apiConfig.branding?.theme || 'performance',
      });
      setGalleryImages(apiConfig.gallery?.images || []);
      setTestimonials(apiConfig.testimonials || []);
    } catch (error) {
      console.error('Failed to load landing page config:', error);
      messageApi.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Only validate the basic required fields, not branding/gallery/testimonials
      const values = await form.validateFields([
        'name',
        'tagline',
        'phone',
        'email',
        'street',
        'city',
        'state',
        'zip',
        'weekday',
        'saturday',
        'sunday',
        'prePurchasePrice'
      ]);

      // Get all form values including optional ones
      const allValues = form.getFieldsValue();
      console.log('Form values:', allValues); // Debug log
      setLoading(true);

      // Prepare API config
      const apiConfig: Partial<ShopLandingConfig> = {
        businessName: values.name,
        tagline: values.tagline,
        phone: values.phone,
        email: values.email,
        address: {
          street: values.street,
          city: values.city,
          state: values.state,
          zip: values.zip,
        },
        hours: {
          weekday: values.weekday,
          saturday: values.saturday,
          sunday: values.sunday,
        },
        prePurchasePrice: values.prePurchasePrice,
        branding: {
          logo: allValues.logo || '',
          heroBackground: allValues.heroBackground || '',
          theme: allValues.theme || 'performance',
        },
        gallery: {
          images: galleryImages,
        },
        testimonials: testimonials,
      };

      // Save to backend API
      await shopLandingConfigApi.update(apiConfig);

      // Also save to localStorage for backward compatibility with landing page
      // (Landing page still reads from localStorage)
      const legacyConfig: ShopConfig = {
        ...shopConfig,
        name: values.name,
        tagline: values.tagline,
        phone: values.phone,
        email: values.email,
        address: {
          street: values.street,
          city: values.city,
          state: values.state,
          zip: values.zip,
        },
        hours: {
          weekday: values.weekday,
          saturday: values.saturday,
          sunday: values.sunday,
        },
        prePurchaseInspection: {
          ...shopConfig.prePurchaseInspection,
          startingPrice: values.prePurchasePrice,
        },
        branding: {
          logo: allValues.logo || '',
          favicon: shopConfig.branding?.favicon || '',
          heroBackground: allValues.heroBackground || '',
          theme: allValues.theme || 'performance',
          customColors: shopConfig.branding?.customColors || {
            primary: '#ef4444',
            secondary: '#111827',
            accent: '#f59e0b',
            background: '#ffffff',
            text: '#111827',
          },
        },
        gallery: {
          enabled: true,
          images: galleryImages,
          categories: shopConfig.gallery?.categories || ['Engine Work', 'Transmission', 'Electrical', 'Brakes', 'Suspension', 'Diagnostics', 'General Repair'],
        },
        testimonials: {
          enabled: true,
          reviews: testimonials,
        },
      };
      saveShopConfig(legacyConfig);

      messageApi.success('Shop configuration saved successfully!');
      setLoading(false);
      onClose();
    } catch (error: any) {
      console.error('Error saving configuration:', error);
      if (error.errorFields) {
        console.error('Validation errors:', error.errorFields);
        const fieldNames = error.errorFields.map((f: any) => f.name[0]).join(', ');
        messageApi.error(`Please fill in required fields: ${fieldNames}`);
      } else {
        messageApi.error(error.message || 'Failed to save configuration');
      }
      setLoading(false);
    }
  };

  const handleAddMedia = () => {
    if (newMediaType === 'video') {
      if (!newVideoId || !newImageTitle) {
        messageApi.error('Please provide both video ID and title');
        return;
      }
      const newVideo = {
        id: Date.now().toString(),
        url: newVideoId,
        title: newImageTitle,
        category: newImageCategory,
        description: '',
        type: 'video' as const,
        videoProvider: newVideoProvider,
      };
      setGalleryImages([...galleryImages, newVideo]);
      setNewVideoId('');
      setNewImageTitle('');
      messageApi.success('Video added to gallery');
    } else {
      if (!newImageUrl || !newImageTitle) {
        messageApi.error('Please provide both image and title');
        return;
      }
      const newImage = {
        id: Date.now().toString(),
        url: newImageUrl,
        title: newImageTitle,
        category: newImageCategory,
        description: '',
        type: 'image' as const,
      };
      setGalleryImages([...galleryImages, newImage]);
      setNewImageUrl('');
      setNewImageTitle('');
      messageApi.success('Image added to gallery');
    }
  };

  const handleAddTestimonial = () => {
    if (!newTestimonialName || !newTestimonialText) {
      messageApi.error('Please provide both name and review text');
      return;
    }
    const newTestimonial = {
      id: Date.now().toString(),
      name: newTestimonialName,
      text: newTestimonialText,
      rating: newTestimonialRating,
      date: newTestimonialDate || new Date().toLocaleDateString(),
    };
    setTestimonials([...testimonials, newTestimonial]);
    setNewTestimonialName('');
    setNewTestimonialText('');
    setNewTestimonialRating(5);
    setNewTestimonialDate('');
    messageApi.success('Testimonial added');
  };

  const handleRemoveTestimonial = (id: string) => {
    setTestimonials(testimonials.filter(t => t.id !== id));
    messageApi.success('Testimonial removed');
  };

  const handleRemoveImage = (id: string) => {
    setGalleryImages(galleryImages.filter(img => img.id !== id));
    messageApi.success('Image removed from gallery');
  };

  const handleReset = () => {
    Modal.confirm({
      title: 'Reset to Defaults',
      content: 'Are you sure you want to reset all shop settings to default values? This cannot be undone.',
      okText: 'Reset',
      okType: 'danger',
      onOk: () => {
        resetShopConfig();
        messageApi.success('Settings reset to defaults! Reload the page to see changes.');
        onClose();
      },
    });
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Modal
      title="Shop Configuration - Landing Page Settings"
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="reset" danger onClick={handleReset}>
          Reset to Defaults
        </Button>,
        <Button key="reload" icon={<ReloadOutlined />} onClick={handleReload}>
          Reload Page
        </Button>,
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={loading} onClick={handleSave}>
          Save Changes
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Tabs defaultActiveKey="business">
          {/* Business Info Tab */}
          <TabPane tab={<span><ShopOutlined /> Business Info</span>} key="business">
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="Shop Name"
                  name="name"
                  rules={[{ required: true, message: 'Shop name is required' }]}
                >
                  <Input placeholder="CNF Auto LLC" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Tagline"
                  name="tagline"
                  rules={[{ required: true, message: 'Tagline is required' }]}
                >
                  <Input placeholder="Dealer-Level Expertise at Independent Shop Prices" />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          {/* Contact Info Tab */}
          <TabPane tab={<span><PhoneOutlined /> Contact</span>} key="contact">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Phone Number"
                  name="phone"
                  rules={[{ required: true, message: 'Phone number is required' }]}
                >
                  <Input placeholder="305-316-0393" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: 'Email is required' },
                    { type: 'email', message: 'Please enter a valid email' },
                  ]}
                >
                  <Input placeholder="info@cnfautosuite.com" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Street Address"
                  name="street"
                  rules={[{ required: true, message: 'Street address is required' }]}
                >
                  <Input placeholder="123 Main Street" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="City"
                  name="city"
                  rules={[{ required: true, message: 'City is required' }]}
                >
                  <Input placeholder="Miami" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="State"
                  name="state"
                  rules={[{ required: true, message: 'State is required' }]}
                >
                  <Input placeholder="FL" maxLength={2} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="ZIP Code"
                  name="zip"
                  rules={[{ required: true, message: 'ZIP code is required' }]}
                >
                  <Input placeholder="33101" />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          {/* Hours Tab */}
          <TabPane tab={<span><ClockCircleOutlined /> Hours</span>} key="hours">
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="Weekday Hours"
                  name="weekday"
                  rules={[{ required: true, message: 'Weekday hours are required' }]}
                >
                  <Input placeholder="8:00 AM - 6:00 PM" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Saturday Hours"
                  name="saturday"
                  rules={[{ required: true, message: 'Saturday hours are required' }]}
                >
                  <Input placeholder="9:00 AM - 4:00 PM" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Sunday Hours"
                  name="sunday"
                  rules={[{ required: true, message: 'Sunday hours are required' }]}
                >
                  <Input placeholder="Closed" />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          {/* Pricing Tab */}
          <TabPane tab="Pricing" key="pricing">
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="Pre-Purchase Inspection Starting Price"
                  name="prePurchasePrice"
                  rules={[{ required: true, message: 'Price is required' }]}
                >
                  <InputNumber
                    prefix="$"
                    placeholder="250"
                    style={{ width: '100%' }}
                    min={0}
                  />
                </Form.Item>
                <p style={{ color: '#6b7280', fontSize: '12px', marginTop: '-16px' }}>
                  This is the starting price displayed on the landing page. Actual price varies by location.
                </p>
              </Col>
            </Row>
          </TabPane>

          {/* Branding Tab */}
          <TabPane tab={<span><BgColorsOutlined /> Branding</span>} key="branding">
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="Shop Logo"
                  name="logo"
                  valuePropName="value"
                  extra={
                    <div>
                      Upload your shop logo (PNG, JPEG, SVG). Displays in header of landing page.
                      <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fef3c7', borderRadius: '4px', fontSize: '12px' }}>
                        <strong>💡 Tip for best results:</strong> Use a PNG with transparent background.
                        If your logo shows a white box, use a free tool like <a href="https://www.remove.bg" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>remove.bg</a> to remove the background.
                      </div>
                    </div>
                  }
                >
                  <ImageUpload
                    maxSize={2}
                    placeholder="Upload Logo"
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Hero Background Image"
                  name="heroBackground"
                  valuePropName="value"
                  extra="Upload a background image for the hero section (workshop, cars, garage, etc.)."
                >
                  <ImageUpload
                    maxSize={5}
                    placeholder="Upload Hero Background"
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Color Theme"
                  name="theme"
                  extra="Choose a pre-designed color theme for your landing page."
                >
                  <Select placeholder="Select a theme">
                    {Object.entries(themePresets).map(([key, preset]) => (
                      <Select.Option key={key} value={key}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div
                            style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '4px',
                              background: preset.gradient,
                              border: '1px solid #e5e7eb',
                            }}
                          />
                          <div>
                            <strong>{preset.name}</strong>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                              {preset.description}
                            </div>
                          </div>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          {/* Gallery Tab */}
          <TabPane tab={<span><PictureOutlined /> Gallery</span>} key="gallery">
            {/* Current Gallery Images */}
            <div style={{ marginBottom: '24px' }}>
              <Title level={5}>Current Gallery ({galleryImages.length} images)</Title>
              {galleryImages.length > 0 ? (
                <List
                  grid={{ gutter: 16, xs: 1, sm: 2, md: 3 }}
                  dataSource={galleryImages}
                  renderItem={(image) => (
                    <List.Item>
                      <Card
                        hoverable
                        cover={
                          <div style={{ height: '150px', overflow: 'hidden', backgroundColor: '#f3f4f6' }}>
                            <img
                              src={image.url}
                              alt={image.title}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </div>
                        }
                        actions={[
                          <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => handleRemoveImage(image.id)}
                          >
                            Remove
                          </Button>,
                        ]}
                      >
                        <Card.Meta
                          title={image.title}
                          description={
                            <div>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                {image.category}
                              </div>
                            </div>
                          }
                        />
                      </Card>
                    </List.Item>
                  )}
                />
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                  <PictureOutlined style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '12px' }} />
                  <p style={{ color: '#6b7280', margin: 0 }}>No images in gallery yet. Add your first work example below.</p>
                </div>
              )}
            </div>

            {/* Add New Media Form */}
            <Card
              title="Add New Work Example"
              style={{ backgroundColor: '#fef3c7', borderColor: '#fbbf24' }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Media Type <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <Radio.Group value={newMediaType} onChange={(e) => setNewMediaType(e.target.value)}>
                    <Radio value="image">
                      <PictureOutlined /> Image
                    </Radio>
                    <Radio value="video">
                      <YoutubeOutlined /> Video
                    </Radio>
                  </Radio.Group>
                </div>

                {newMediaType === 'image' ? (
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                      Image <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <ImageUpload
                      value={newImageUrl}
                      onChange={setNewImageUrl}
                      maxSize={5}
                      placeholder="Upload Work Photo"
                    />
                  </div>
                ) : (
                  <>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                        Video Platform <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <Select value={newVideoProvider} onChange={setNewVideoProvider} style={{ width: '100%' }}>
                        <Select.Option value="youtube">
                          <YoutubeOutlined /> YouTube
                        </Select.Option>
                        <Select.Option value="vimeo">
                          <VideoCameraOutlined /> Vimeo
                        </Select.Option>
                      </Select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                        Video ID <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <Input
                        placeholder={newVideoProvider === 'youtube' ? 'e.g., dQw4w9WgXcQ' : 'e.g., 123456789'}
                        value={newVideoId}
                        onChange={(e) => setNewVideoId(e.target.value)}
                      />
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        {newVideoProvider === 'youtube'
                          ? 'Copy the ID from youtube.com/watch?v=VIDEO_ID'
                          : 'Copy the ID from vimeo.com/VIDEO_ID'}
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Title <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <Input
                    placeholder="e.g., Mercedes Engine Rebuild, BMW Transmission Service"
                    value={newImageTitle}
                    onChange={(e) => setNewImageTitle(e.target.value)}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Category <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <Select
                    value={newImageCategory}
                    onChange={setNewImageCategory}
                    style={{ width: '100%' }}
                  >
                    {shopConfig.gallery?.categories.map((cat) => (
                      <Select.Option key={cat} value={cat}>
                        {cat}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddMedia}
                  block
                >
                  Add to Gallery
                </Button>
              </Space>
            </Card>
          </TabPane>

          {/* Testimonials Tab */}
          <TabPane tab={<span><StarOutlined /> Testimonials</span>} key="testimonials">
            {/* Current Testimonials */}
            <div style={{ marginBottom: '24px' }}>
              <Title level={5}>Customer Reviews ({testimonials.length} testimonials)</Title>
              {testimonials.length > 0 ? (
                <List
                  dataSource={testimonials}
                  renderItem={(testimonial) => (
                    <List.Item
                      actions={[
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveTestimonial(testimonial.id)}
                        >
                          Remove
                        </Button>,
                      ]}
                    >
                      <List.Item.Meta
                        title={
                          <div>
                            <span style={{ fontWeight: 600 }}>{testimonial.name}</span>
                            <Rate disabled defaultValue={testimonial.rating} style={{ marginLeft: '12px', fontSize: '14px' }} />
                          </div>
                        }
                        description={
                          <div>
                            <div style={{ marginBottom: '4px', fontStyle: 'italic' }}>"{testimonial.text}"</div>
                            <div style={{ fontSize: '12px', color: '#9ca3af' }}>{testimonial.date}</div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                  <StarOutlined style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '12px' }} />
                  <p style={{ color: '#6b7280', margin: 0 }}>No testimonials yet. Add your first customer review below.</p>
                </div>
              )}
            </div>

            {/* Add New Testimonial Form */}
            <Card
              title="Add New Testimonial"
              style={{ backgroundColor: '#f0f9ff', borderColor: '#3b82f6' }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Customer Name <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <Input
                    placeholder="e.g., John Smith"
                    value={newTestimonialName}
                    onChange={(e) => setNewTestimonialName(e.target.value)}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Review Text <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <TextArea
                    rows={3}
                    placeholder="e.g., Excellent service! They diagnosed and fixed my BMW's engine issue quickly and professionally."
                    value={newTestimonialText}
                    onChange={(e) => setNewTestimonialText(e.target.value)}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Rating <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <Rate value={newTestimonialRating} onChange={setNewTestimonialRating} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Date (Optional)
                  </label>
                  <Input
                    placeholder="e.g., January 2025 or leave blank for today"
                    value={newTestimonialDate}
                    onChange={(e) => setNewTestimonialDate(e.target.value)}
                  />
                </div>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddTestimonial}
                  block
                >
                  Add Testimonial
                </Button>
              </Space>
            </Card>
          </TabPane>
        </Tabs>

        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fef3c7', borderRadius: '8px' }}>
          <p style={{ margin: 0, fontSize: '13px', color: '#92400e' }}>
            <strong>Note:</strong> After saving changes, you may need to reload the page to see updates on the landing page.
            Services and brands can be edited in the code (frontend/src/config/shop.config.ts).
          </p>
        </div>
      </Form>
    </Modal>
  );
};

export default ShopConfigModal;
